/**
 * Zod schemas for agent-runs API validation.
 *
 * Mirrors `backend/features/agent_runs/schemas.py` (RunResponse,
 * RunDetailResponse, RunEventResponse) field for field. Enum values are
 * taken from the actual constraints enforced there: `RunEventIn.type`'s
 * pattern, the `kind` pattern on `DispatchRequest`, and
 * `run_service.TERMINAL_STATUSES` plus the two non-terminal statuses
 * (`pending`, `running`) used elsewhere in that module.
 */

import { z } from 'zod';

export const RunStatusSchema = z.enum([
  'pending',
  'running',
  'succeeded',
  'failed',
  'denied',
  'stopped',
  'expired',
]);

export const RunKindSchema = z.enum(['review', 'chore', 'fix', 'implement']);

export const IdentityModeSchema = z.enum(['bot', 'brokered']);

export const RunEventTypeSchema = z.enum([
  'phase',
  'file_change',
  'command',
  'finding',
  'error',
]);

export const AgentRunEventSchema = z.object({
  seq: z.number(),
  ts: z.string(),
  type: RunEventTypeSchema,
  payload: z.record(z.unknown()),
});

/**
 * What the run's agent spent through the AI gateway (backend
 * `run_usage_service.py`). `budget_tokens_used` is what `budget_tokens` is
 * measured against: uncached input + output + cache writes, not cache
 * reads. Trails the agent by a few seconds (the gateway records off the
 * request path).
 */
export const RunUsageSchema = z.object({
  requests: z.number(),
  tokens_in: z.number(),
  tokens_out: z.number(),
  cache_read_tokens: z.number(),
  cache_write_tokens: z.number(),
  budget_tokens_used: z.number(),
  cost_usd: z.number().nullable(),
});

/**
 * The agent's own account of a run (`result.report`, posted to `/finish`).
 * Model-generated: `RunResult` safe-parses it and renders it as plain text.
 * The bounds match the backend's (`schemas.RunReport`), which drops a report
 * outside them before it is stored.
 */
const REPORT_STRING_MAX = 500;
const REPORT_ITEMS_MAX = 50;
const reportString = z.string().max(REPORT_STRING_MAX);

export const RunReportSchema = z.object({
  summary: reportString,
  addressed: z.array(reportString).max(REPORT_ITEMS_MAX),
  not_addressed: z
    .array(z.object({ item: reportString, reason: reportString }))
    .max(REPORT_ITEMS_MAX),
  needs_human: z.array(reportString).max(REPORT_ITEMS_MAX),
});

export const AgentRunSchema = z.object({
  id: z.string(),
  project_id: z.string(),
  kind: RunKindSchema,
  ref: z.string(),
  status: RunStatusSchema,
  identity_mode: IdentityModeSchema,
  degraded_reason: z.string().nullable(),
  pipeline_id: z.number().nullable(),
  job_id: z.number().nullable(),
  result: z.record(z.unknown()).nullable(),
  started_at: z.string().nullable(),
  finished_at: z.string().nullable(),
  created_at: z.string(),
  // Limits. What was actually spent is `usage`, below.
  budget_seconds: z.number(),
  budget_tokens: z.number(),
  // Derived server-side (see backend `run_links_service.py`) from
  // Project -> GitServer, never built on the frontend: this feature has
  // no GitLab base URL of its own to build one from. `project_path` is
  // GitLab's `path_with_namespace`, e.g. "group/subproject". All four are
  // nullable -- a project/server that can't be resolved, or a null
  // pipeline_id/job_id/absent result.mr_iid, degrades to null rather than
  // a malformed URL.
  project_path: z.string().nullable(),
  pipeline_url: z.string().nullable(),
  job_url: z.string().nullable(),
  mr_url: z.string().nullable(),
  // Null until the run's agent has made a recorded gateway request.
  usage: RunUsageSchema.nullable().optional(),
});

export const AgentRunDetailSchema = AgentRunSchema.extend({
  events: z.array(AgentRunEventSchema),
  // Whether this viewer may stop the run (its owner, or an admin). A project
  // reader can open another user's run but Stop stays owner-or-admin.
  can_stop: z.boolean(),
});

export const RunListResponseSchema = z.object({
  runs: z.array(AgentRunSchema),
});

export type RunStatus = z.infer<typeof RunStatusSchema>;
export type RunKind = z.infer<typeof RunKindSchema>;
export type IdentityMode = z.infer<typeof IdentityModeSchema>;
export type RunEventType = z.infer<typeof RunEventTypeSchema>;
export type AgentRunEvent = z.infer<typeof AgentRunEventSchema>;
export type AgentRun = z.infer<typeof AgentRunSchema>;
export type AgentRunDetail = z.infer<typeof AgentRunDetailSchema>;
export type RunReport = z.infer<typeof RunReportSchema>;
export type RunUsage = z.infer<typeof RunUsageSchema>;
